import Locator from "@arcgis/core/tasks/Locator";
import MapView from "@arcgis/core/views/MapView";
import Locate from "@arcgis/core/widgets/Locate";
import { consoleSuccessStyle } from "../../../shared/constants/logging.constants";
import { LoggingService } from "../../../shared/services/Logging.service";
import {
  setUserLocationError,
  updateUserLocation,
  updateUserLocationError,
} from "../../../state-management/slices/global/global.slice";
import { AppDispatch } from "../../../state-management/store";
import { LOCATOR_URL } from "../Map.constants";
import { IUserLocation } from "../Map.types";
import MapUtils from "../Map.utils";

/*
 * @export
 * @class LocationWidget
 */
export class LocationWidget {
  private static instance: LocationWidget;
  private dispatch: AppDispatch;
  private mapView: MapView;
  private loggingService: LoggingService;
  private locatorTask: Locator;
  private locate!: Locate;

  /**
   * Creates an instance of LocationWidget.
   * @param {MapView} mapView
   * @param {AppDispatch} dispatch
   * @memberof LocationWidget
   */
  private constructor(mapView: MapView, dispatch: AppDispatch) {
    this.mapView = mapView;
    this.dispatch = dispatch;

    this.locatorTask = new Locator({
      url: LOCATOR_URL,
    });

    this.loggingService = LoggingService.getInstance(dispatch);
    this.buildLocationWidget();
  }

  /**
   * @private buildLocationWidget
   * @memberof LocationWidget
   */
  private buildLocationWidget(): void {
    this.locate = new Locate({
      container: "locationContainer",
      view: this.mapView,
      useHeadingEnabled: false,
      iconClass: "esri-icon-navigation",
      goToOverride: function (view, options) {
        options.target.scale = 1500;
        return view.goTo(options.target);
      },
    });

    this.mapView.ui.add(this.locate, {
      position: "top-left",
      index: 200,
    });

    // eslint-disable-next-line no-console
    console.info("%cLocation Widget Initialized", consoleSuccessStyle);
  }

  /**
   * getLocate
   * @return {*}  {Locate}
   * @memberof LocationWidget
   */
  public getLocate(): Locate {
    return this.locate;
  }

  /**
   * getLocatorTask
   * @return {*}  {Locator}
   * @memberof LocationWidget
   */
  public getLocatorTask(): Locator {
    return this.locatorTask;
  }

  /**
   * getUserLocation
   * @memberof LocationWidget
   */
  public getUserLocation(): void {
    this.locate
      .locate()
      .then((position: GeolocationPosition) => {
        const userLocation: IUserLocation = {
          timestamp: position.timestamp,
          lat: position.coords.latitude,
          lng: position.coords.longitude,
        };

        this.dispatch(updateUserLocation(userLocation));
      })
      .catch((error: GeolocationPositionError) => {
        const locationError = MapUtils.getLocationError(error);
        // @todo: Add thunk to add logging of errors.
        this.dispatch(updateUserLocationError(locationError));
      });
  }

  /**
   * @static getInstance
   * @param {MapView} mapView
   * @param {AppDispatch} dispatch
   * @return {*}  {LocationWidget}
   * @memberof LocationWidget
   */
  public static getInstance(
    mapView: MapView,
    dispatch: AppDispatch
  ): LocationWidget {
    if (!LocationWidget.instance) {
      LocationWidget.instance = new LocationWidget(mapView, dispatch);
    }

    return LocationWidget.instance;
  }
}
